/**
 * Which agent type a flow node launches with.
 *
 * Shared by the runtime (`server/adapters/agent-type-resolution.ts`) and the
 * flow editor (`web/useFlowRuntimeAgentType.ts`) so the chip on the canvas
 * names the agent the run will actually use.
 *
 * @module @reflex/plugin-flows/shared/agent-type-resolution
 */

/**
 * Last-resort agent type for a node that pins neither an agent type nor a
 * resolvable model. Only reached when the model-support catalog is
 * unavailable (or knows no launchable agent), so it stays a constant rather
 * than a configurable default.
 */
export const FLOW_FALLBACK_AGENT_TYPE = 'claude-code';

/**
 * The slice of a model-support entry this rule reads. Structurally satisfied
 * by both the server's `AgentModelSupportEntry` and the web's
 * `AgentModelSupportMatrixEntry`.
 */
export interface FlowAgentTypeCatalogEntry {
  status: string;
  discoveredModels?: readonly { id: string }[];
}

/** Agent types keyed by agent type, plus the org's launch default. */
export interface FlowAgentTypeCatalogSnapshot {
  agents?: Record<string, FlowAgentTypeCatalogEntry | undefined>;
  defaultAgentType?: string;
}

export interface PickFlowAgentTypeParams {
  /** Agent type pinned on the node (or inherited from its persona). */
  pinned?: string | null;
  /** Model pinned on the node, used to infer the agent type that can run it. */
  model?: string | null;
  /** Loaded catalog; `null`/`undefined` while it is unavailable. */
  catalog?: FlowAgentTypeCatalogSnapshot | null;
}

/**
 * Resolve the launch agent type for a flow node.
 *
 * A node may pin a model while inheriting its agent type ("Default
 * (inherit)"), and models are agent-type-scoped:
 * `nebius/dedicated/runloop/...` exists only in OpenCode's catalog,
 * `claude-*` only in Claude Code's. Defaulting the agent type to a constant
 * while honoring the pinned model produced launches the runner rejects with
 * "Model '<id>' is not in the discovered model catalog for agent type
 * '<other agent>'". So a pinned model picks its own runtime:
 *
 * 1. An explicit pin wins — a node that names its agent type gets it.
 * 2. Otherwise an agent type whose discovered catalog contains the pinned
 *    model, preferring the org's launch default when it is one of them (ids
 *    are vendor-prefixed per agent, so overlap is rare but possible).
 * 3. Otherwise the org's launch-default agent type.
 * 4. Otherwise {@link FLOW_FALLBACK_AGENT_TYPE}.
 */
export function pickFlowAgentType(params: PickFlowAgentTypeParams): string {
  const { pinned, model, catalog } = params;

  if (pinned) return pinned;
  if (!catalog) return FLOW_FALLBACK_AGENT_TYPE;

  const orgDefault = catalog.defaultAgentType;

  if (model) {
    // Entry order is registration order — the same order the launcher lists
    // agents in — so an overlapping model resolves the way the picker would.
    const owners = Object.entries(catalog.agents ?? {})
      .filter(
        ([, entry]) =>
          entry?.status === 'available' &&
          (entry.discoveredModels ?? []).some((m) => m.id === model),
      )
      .map(([agentType]) => agentType);

    if (owners.length > 0) {
      return orgDefault && owners.includes(orgDefault) ? orgDefault : owners[0]!;
    }
  }

  return orgDefault ?? FLOW_FALLBACK_AGENT_TYPE;
}
